import React, { useEffect, useState } from 'react';
import { MultiSelect } from '../components/MultiSelect';
import { FieldsTable } from '../components/FieldsTable';
import { EditionSelector } from '../components/EditionSelector';
import { PlanSelector } from '../components/PlanSelector';
import { SmartAskAmountsInfo } from '../components/SmartAskAmountsInfo';
import { getUniqueProducts, getUniqueFeatures, getFilteredFields, getFieldsByProduct } from '../data/fieldRequirements';
import { getEditionByName } from '../data/editionData';
import { getPlanByName } from '../data/planData';
import { getProductOptions } from '../data/productMapping';
type FilterMode = 'product-feature' | 'edition' | 'plan' | 'vm';
export function FieldRequirementsTool() {
  const [filterMode, setFilterMode] = useState<FilterMode>('product-feature');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedEdition, setSelectedEdition] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [includeGO, setIncludeGO] = useState(false);
  const [includeVM, setIncludeVM] = useState(false);
  const [includeEvents, setIncludeEvents] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);
  // Get product options with grouping
  const productOptions = getProductOptions();
  // Update available features when products change
  useEffect(() => {
    if (filterMode === 'product-feature') {
      const features = getUniqueFeatures(selectedProducts);
      setAvailableFeatures(features);
      // Auto-select all features for products that have data-powered features
      const productsWithAutoFeatures = ['Outreach', 'Events', 'Online Giving'];
      const selectedProductsWithFeatures = selectedProducts.filter(p => productsWithAutoFeatures.includes(p));
      if (selectedProductsWithFeatures.length > 0) {
        const autoSelectedFeatures = features.filter(feature => {
          return selectedProductsWithFeatures.some(product => {
            const productFeatures = getUniqueFeatures([product]);
            return productFeatures.includes(feature);
          });
        });
        const allSelectedFeatures = Array.from(new Set([...selectedFeatures, ...autoSelectedFeatures]));
        if (JSON.stringify(allSelectedFeatures.sort()) !== JSON.stringify(selectedFeatures.sort())) {
          setSelectedFeatures(allSelectedFeatures);
        }
      } else {
        setSelectedFeatures(prev => prev.filter(feature => features.includes(feature)));
      }
    }
  }, [selectedProducts, filterMode]);
  // Handle edition selection and GO/VM toggles
  useEffect(() => {
    if (filterMode === 'edition' && selectedEdition) {
      const edition = getEditionByName(selectedEdition);
      if (edition) {
        const editionProducts = edition.productFeatures.map(pf => pf.product);
        const editionFeatures = edition.productFeatures.flatMap(pf => pf.features);
        const products = [...editionProducts];
        const features = [...editionFeatures];
        if (includeGO && !products.includes('GO')) {
          products.push('GO');
          features.push('GO');
        }
        if (includeVM && !products.includes('VM')) {
          products.push('VM');
          features.push('Core');
        }
        setSelectedProducts(products);
        setSelectedFeatures(features);
      }
    }
  }, [selectedEdition, includeGO, includeVM, filterMode]);
  // Handle plan selection and add-on toggles
  useEffect(() => {
    if (filterMode === 'plan' && selectedPlan) {
      const plan = getPlanByName(selectedPlan);
      if (plan) {
        const planProducts = plan.productFeatures.map(pf => pf.product);
        const planFeatures = plan.productFeatures.flatMap(pf => pf.features);
        const products = [...planProducts];
        const features = [...planFeatures];
        if (includeEvents && !products.includes('Events')) {
          products.push('Events');
          features.push('Constituent ID Matching');
        }
        if (includeGO && !products.includes('GO')) {
          products.push('GO');
          features.push('GO');
        }
        if (includeVM && !products.includes('VM')) {
          products.push('VM');
          features.push('Core');
        }
        setSelectedProducts(products);
        setSelectedFeatures(features);
      }
    }
  }, [selectedPlan, includeEvents, includeGO, includeVM, filterMode]);
  // Handle VM mode - auto-select VM product and Core feature
  useEffect(() => {
    if (filterMode === 'vm') {
      setSelectedProducts(['VM']);
      setSelectedFeatures(['Core']);
    }
  }, [filterMode]);
  const handleEditionSelect = (editionName: string) => {
    setSelectedEdition(editionName);
  };
  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
  };
  const handleToggleGO = () => {
    setIncludeGO(!includeGO);
  };
  const handleToggleVM = () => {
    setIncludeVM(!includeVM);
  };
  const handleToggleEvents = () => {
    setIncludeEvents(!includeEvents);
  };
  const handleTabChange = (mode: FilterMode) => {
    setFilterMode(mode);
    if (mode === 'product-feature') {
      setSelectedEdition(null);
      setSelectedPlan(null);
      setIncludeGO(false);
      setIncludeVM(false);
      setIncludeEvents(false);
      setSelectedProducts([]);
      setSelectedFeatures([]);
    } else if (mode === 'edition') {
      setSelectedPlan(null);
      setIncludeEvents(false);
      setSelectedProducts([]);
      setSelectedFeatures([]);
    } else if (mode === 'plan') {
      setSelectedEdition(null);
      setSelectedProducts([]);
      setSelectedFeatures([]);
    } else if (mode === 'vm') {
      setSelectedEdition(null);
      setSelectedPlan(null);
      setIncludeGO(false);
      setIncludeVM(false);
      setIncludeEvents(false);
    }
  };
  const filteredFields = getFilteredFields(selectedProducts, selectedFeatures);
  const {
    vmFields,
    otherFields
  } = getFieldsByProduct(selectedProducts, selectedFeatures);
  // For VM mode, we don't need tabs in the table
  const showTabs = filterMode !== 'vm' && selectedProducts.includes('VM') && selectedProducts.some(p => p !== 'VM');
  // Check if GO is selected
  const hasGO = selectedProducts.includes('GO');
  // Check if Wealth is selected
  const hasWealth = selectedProducts.includes('Wealth');
  // Check if VM Reimagined is selected
  const hasVMReimagined = selectedProducts.includes('VM Reimagined');
  // Check if Smart Ask Amounts is selected
  const hasSmartAskAmounts = selectedFeatures.includes('Smart Ask Amounts');
  const handleClearAll = () => {
    if (filterMode === 'vm') {
      // In VM mode, don't clear - just stay on VM
      return;
    }
    setSelectedProducts([]);
    setSelectedFeatures([]);
    setSelectedEdition(null);
    setSelectedPlan(null);
    setIncludeGO(false);
    setIncludeVM(false);
    setIncludeEvents(false);
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-314" data-mp="2kx9ouz5f0g0t">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-315" data-mp="2kxjcwh6wgd3v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6" data-id="element-316" data-mp="2pw2zmjtbhsxa">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900" data-id="element-317" data-mp="2uv5xnkavqyvr">
            Data Requirements Interactive Resource
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-318" data-mp="2ztqxhim7zgjo">
            Select products and features to view required and recommended fields
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-id="element-319" data-mp="38o5d5manwt3b">
        {/* Tab Navigation - Scrollable on mobile */}
        <div className="bg-white rounded-t-lg border border-b-0 border-gray-200" data-id="element-320" data-mp="3dnrn9mv71t7w">
          <div className="flex overflow-x-auto border-b border-gray-200 -mb-px scrollbar-hide" data-id="element-321" data-mp="3imusb7b6bzfv">
            <button onClick={() => handleTabChange('product-feature')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'product-feature' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-322" data-mp="3nkctucmv3cdu">
              Product/Feature
            </button>
            <button onClick={() => handleTabChange('edition')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'edition' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-323" data-mp="3p6ssk5najflq">
              Edition
            </button>
            <button onClick={() => handleTabChange('plan')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'plan' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-324" data-mp="36fszotcodln6">
              <span className="hidden sm:inline" data-id="element-325" data-mp="17yr6bpffuu2m">Plan (old pricing)</span>
              <span className="sm:hidden" data-id="element-326" data-mp="180lqv2mssk66">Plan</span>
            </button>
            <button onClick={() => handleTabChange('vm')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'vm' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-327" data-mp="1cynapps1snip">
              <span className="hidden sm:inline" data-id="element-328" data-mp="1vo3d6inamq1r">Volunteer Management</span>
              <span className="sm:hidden" data-id="element-329" data-mp="1vq6dpwm2o1kq">VM</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-b-lg border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6" data-id="element-330" data-mp="k8wr517nctf7">
          {filterMode === 'product-feature' ? <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-331" data-mp="1fhmwy4aysd6f">
                <div data-id="element-332" data-mp="1ke3koxe94piz">
                  <MultiSelect label="Products" options={productOptions} selectedValues={selectedProducts} onChange={setSelectedProducts} placeholder="Select products..." grouped={true} data-id="element-333" data-mp="1kgppwqdo6sy1" />
                  <p className="mt-2 text-xs text-gray-500" data-id="element-334" data-mp="10qe0pcck3tkb">
                    Selecting a product will select all its data-powered
                    features by default
                  </p>
                  {hasGO && <p className="mt-1 text-xs text-gray-500" data-id="element-335" data-mp="rr0owd0ymswf">
                      GO's data needs are foundational, not feature specific, so
                      no GO specific features are listed
                    </p>}
                  {hasWealth && <p className="mt-1 text-xs text-gray-500" data-id="element-336" data-mp="ag3tiyeo25da">
                      Wealth's data needs are foundational, not feature
                      specific, so no Wealth specific features are listed
                    </p>}
                  {hasVMReimagined && <p className="mt-1 text-xs text-gray-500" data-id="element-337" data-mp="ffzxwyr9zytd">
                      Volunteer Management: Reimagined has a different set of
                      requirements than the original Volunteer Management
                      System. For the original, please use the tab labeled
                      "Volunteer Management".
                    </p>}
                </div>

                <MultiSelect label="Features" options={availableFeatures} selectedValues={selectedFeatures} onChange={setSelectedFeatures} placeholder="Select features (optional)..." disabled={selectedProducts.length === 0} data-id="element-338" data-mp="y6hfc76nr7zf" />
              </div>

              {/* Smart Ask Amounts Message */}
              {hasSmartAskAmounts && <SmartAskAmountsInfo data-id="element-339" data-mp="3tnttfi2jkzzx" />}
            </> : filterMode === 'edition' ? <EditionSelector selectedEdition={selectedEdition} onSelectEdition={handleEditionSelect} includeGO={includeGO} includeVM={includeVM} onToggleGO={handleToggleGO} onToggleVM={handleToggleVM} data-id="element-340" data-mp="2g8zdb5jdh8t" /> : filterMode === 'plan' ? <PlanSelector selectedPlan={selectedPlan} onSelectPlan={handlePlanSelect} includeEvents={includeEvents} includeVM={includeVM} includeGO={includeGO} onToggleEvents={handleToggleEvents} onToggleVM={handleToggleVM} onToggleGO={handleToggleGO} data-id="element-341" data-mp="gefbt2bbmefg" /> :
        // VM Mode Content
        <div className="space-y-4" data-id="element-342" data-mp="2kncqs273uuzt">
              <div data-id="element-343" data-mp="2koecr1pnt1wb">
                <h3 className="text-base font-semibold text-gray-900 mb-2" data-id="element-344" data-mp="2kkqk04s1gjin">
                  Volunteer Management Field Requirements
                </h3>
                <p className="text-sm text-gray-500" data-id="element-345" data-mp="2apov9a1odjgl">
                  Volunteer Management (VM) requires a separate data upload from
                  other products. The fields below show all required and
                  recommended data for VM functionality.
                </p>
              </div>

              <div className="bg-[#EFF6FF] border border-[#0047AB]/20 rounded-lg p-4" data-id="element-346" data-mp="2yf9hq2x1eyum">
                <div className="flex items-start gap-3" data-id="element-347" data-mp="2tmq10x31znll">
                  <div className="flex-shrink-0 w-5 h-5 bg-[#0047AB] rounded-full flex items-center justify-center mt-0.5" data-id="element-348" data-mp="2tl5799okc8os">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-349" data-mp="3m4c6rvu03tz5">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-350" data-mp="31u16mmunb01i" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-351" data-mp="2wwhtjq9ootb5">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-352" data-mp="3klu3iscu55ei">
                      Separate Upload Required
                    </h4>
                    <p className="text-sm text-gray-700" data-id="element-353" data-mp="3pk3ueqrj1jub">
                      VM data is uploaded separately from other product data.
                      Review the fields below to ensure your VM data file
                      includes all required and recommended information.
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {filterMode !== 'vm' && (selectedProducts.length > 0 || selectedFeatures.length > 0) && <div className="flex justify-end mt-4" data-id="element-354" data-mp="1wu2y57okqd6r">
                <button onClick={handleClearAll} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0047AB]" data-id="element-355" data-mp="1rw1hetemepyg">
                  Clear All
                </button>
              </div>}
        </div>

        {/* Results */}
        <FieldsTable fields={filteredFields} vmFields={vmFields} otherFields={otherFields} selectedProducts={selectedProducts} selectedFeatures={selectedFeatures} availableFeatures={availableFeatures} showTabs={showTabs} data-id="element-356" data-mp="18z0c17h5mntz" />
      </div>
    </div>;
}